/* Formatage selon la langue CHOISIE sur le site (i18n.language), et non selon
   celle du navigateur : un client sur /zh avec un Chrome anglais doit voir
   « 2026/10/30 15:05 », pas « 10/30/2026, 3:05 PM ». */
import i18n from "../lang/i18n";
import countriesCode from "i18n-iso-countries";
import english from "i18n-iso-countries/langs/en.json";

countriesCode.registerLocale(english);

/** Langue i18next ("zh", "ar"...) -> etiquette BCP 47 pour Intl / toLocale*. */
export const intlLocale = (lang) => {
  const l = String(lang || i18n.language || "en").toLowerCase().split(/[-_]/)[0];
  if (l === "zh") return "zh-CN";
  // Chiffres latins en arabe : prix, numeros et identifiants restent en 0-9 partout.
  if (l === "ar") return "ar-u-nu-latn";
  return l;
};

const toDate = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d) ? null : d;
};

/** Date seule (jour/mois/annee) dans la langue du site. */
export const fmtDate = (value, opts, lang) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString(intlLocale(lang), opts) : "";
};

/** Heure seule dans la langue du site. */
export const fmtTime = (value, opts, lang) => {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(intlLocale(lang), opts) : "";
};

/** Date + heure dans la langue du site. */
export const fmtDateTime = (value, opts, lang) => {
  const d = toDate(value);
  return d ? d.toLocaleString(intlLocale(lang), opts) : "";
};

/** Nombre dans la langue du site (ex. compact : 500K / 50万 / 500 k). */
export const fmtNumber = (n, opts, lang) => {
  try {
    return new Intl.NumberFormat(intlLocale(lang), opts).format(n);
  } catch (e) {
    return String(n);
  }
};

const regionNames = {};
const regionNamesFor = (locale) => {
  if (!(locale in regionNames)) {
    try {
      regionNames[locale] =
        typeof Intl !== "undefined" && Intl.DisplayNames
          ? new Intl.DisplayNames([locale], { type: "region" })
          : null;
    } catch (e) {
      regionNames[locale] = null;
    }
  }
  return regionNames[locale];
};

/** Nom du pays dans la langue du site a partir du code ISO alpha-2.
    `fallback` (nom anglais ou slug du serveur) si le code est absent ou inconnu. */
export const countryName = (code, fallback, lang) => {
  const c = String(code || "").trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(c)) {
    try {
      const dn = regionNamesFor(intlLocale(lang));
      const name = dn && dn.of(c);
      if (name && name !== c) return name;
    } catch (e) {
      /* code inconnu d'Intl : on garde le repli */
    }
  }
  return fallback || c || "";
};

// Slugs du backend qui ne sont pas des noms anglais standard.
const SLUG_CODES = {
  usa: "US", us: "US", unitedstates: "US", "united-states": "US",
  uk: "GB", england: "GB", unitedkingdom: "GB", "united-kingdom": "GB", "great-britain": "GB",
  russia: "RU", czech: "CZ", "czech-republic": "CZ", korea: "KR", "south-korea": "KR",
  vietnam: "VN", laos: "LA", moldova: "MD", bolivia: "BO", venezuela: "VE", iran: "IR",
  syria: "SY", tanzania: "TZ", taiwan: "TW", "hong-kong": "HK", hongkong: "HK",
  macau: "MO", macao: "MO", "ivory-coast": "CI", ivorycoast: "CI", "cote-divoire": "CI",
  drc: "CD", congo: "CG", "dr-congo": "CD", kosovo: "XK", palestine: "PS",
  "north-macedonia": "MK", macedonia: "MK", turkey: "TR", "saudi-arabia": "SA", uae: "AE",
  "united-arab-emirates": "AE", bosnia: "BA", "bosnia-and-herzegovina": "BA",
  // Slugs colles du backend (public/svc de front-next) que getAlpha2Code ne reconnait pas
  bih: "BA", southafrica: "ZA", saudiarabia: "SA", antiguaandbarbuda: "AG", burkinafaso: "BF",
  caf: "CF", capeverde: "CV", costarica: "CR", dominicana: "DO", easttimor: "TL",
  equatorialguinea: "GQ", frenchguiana: "GF", "guinea-bissau": "GW", newcaledonia: "NC",
  newzealand: "NZ", northmacedonia: "MK", puertorico: "PR", saintkittsandnevis: "KN",
  saintlucia: "LC", saintvincentandgrenadines: "VC", salvador: "SV", sierraleone: "SL",
  southsudan: "SS", srilanka: "LK", papuanewguinea: "PG", trinidad: "TT",
};

/** Code ISO alpha-2 deduit d'un slug pays du backend ("usa", "england", "indonesia"...). */
export const codeFromSlug = (slug) => {
  const key = String(slug || "").trim().toLowerCase();
  if (!key) return "";
  if (SLUG_CODES[key]) return SLUG_CODES[key];
  const compact = key.replace(/[\s_]+/g, "-");
  if (SLUG_CODES[compact]) return SLUG_CODES[compact];
  const label = compact
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  try {
    return countriesCode.getAlpha2Code(label, "en") || "";
  } catch (e) {
    return "";
  }
};

/** Nom localise a partir d'un slug pays seul (historique, motifs de solde...). */
export const countryNameFromSlug = (slug, lang) => {
  const raw = String(slug || "").trim();
  return countryName(codeFromSlug(raw), raw, lang);
};
